import { SessionFormFieldError, SessionResource } from '@uplift/api';
import { Diagnosis, YesNo } from '@uplift/types';
import { FormikErrors, FormikTouched } from 'formik';
import * as Yup from 'yup';
import isEqual from 'lodash/isEqual';
import { checkIfFormDisabledByYesNoToggle, checkIfFormIsOnTemplate, deserializeYesNoFieldKey, dynamicFormToTemplateMapping, getDynamicFormsObjectFromFormObject, getFieldTypeHelpers, getYesNoFieldKeyFromFieldKey, getYesNoFromBooleanValue } from './helpers';
import { ClinicalNotesDynamicFormFieldHash, ClinicalNotesDynamicFormKeyType, ClinicalNotesFormType, ClinicalNotesTemplateType, DynamicYesNoKeyHash } from './types';
import { l10nEnService } from '@uplift/services';

export const getClinicalNotesValidationSchema = (
  dynamicYesNoHash: DynamicYesNoKeyHash,
  l10n: typeof l10nEnService,
  diagnoses: Diagnosis[],
  isDiagnosisReviewRequired?: boolean,
  isTreatmentReviewRequired?: boolean,
  isFollowUpTreatmentGoalReviewRequired?: boolean,
) => {
  const yesNoValidation = Yup.mixed<YesNo>().nullable().oneOf([ YesNo.Yes, YesNo.No ], l10n.clinicalNotes.validations.chooseAnOption);

  const dynamicYesNoSchema = Object.keys(dynamicYesNoHash).reduce((r, yesNoKey) => {
    const { parentKey } = deserializeYesNoFieldKey(yesNoKey);

    const mapsToTemplate = dynamicFormToTemplateMapping?.[parentKey] ?? null;

    if (!mapsToTemplate) {
      return r.concat(Yup.object({ [yesNoKey]: yesNoValidation }));
    }

    const customSchema = Yup.object({
      [yesNoKey]: Yup.mixed<YesNo>()
        .when('template', {
          is: mapsToTemplate,
          then: (schema) => schema.oneOf([ YesNo.Yes, YesNo.No ], l10n.clinicalNotes.validations.chooseAnOption),
          otherwise: (schema) => schema.nullable(),
        }),
    });

    return r.concat(customSchema);
  }, Yup.object());

  return Yup.object().shape({
    additionalDetails: Yup.string().transform(v => v.trim()).min(75, 'At least 75 characters required').nullable().required('Required'),
    template: Yup.mixed<ClinicalNotesTemplateType>().oneOf([ 'initial', 'followUp' ]),

    // yesno validation
    mentalStatusExamWithinNormalRangeYesNo: yesNoValidation,
    riskAssessmentDenyYesNo: yesNoValidation,
    treatmentHomeworkYesNo: yesNoValidation,
    // end yesno validation

    // diagnosis
    diagnosis: Yup.array().of(Yup.string()).min(1, l10n.clinicalNotes.validations.diagnosis).test('diagnosis-has-f-code', l10n.clinicalNotes.validations.diagnosisFCode, (value) => {
      const selectedDiagnoses = diagnoses.filter((diagnosis) => value.includes(diagnosis.id));
      const hasAtLeastOneFCode = selectedDiagnoses.some(v => v.code.startsWith('F'));

      if (!hasAtLeastOneFCode) {
        return false;
      }

      return true;
    }),

    identityVerified: Yup.bool().nullable().oneOf([ true ], ' ').required(' '),

    // treatment
    isDiagnosisReviewed: Yup.bool().nullable().when([], {
      is: () => !!isDiagnosisReviewRequired,
      then: (schema) => schema.oneOf([ true ], ' ').required(' '),
    }),
    isTreatmentReviewed: Yup.bool().nullable().when([], {
      is: () => !!isTreatmentReviewRequired,
      then: (schema) => schema.oneOf([ true ], ' ').required(' '),
    }),
    isFollowUpTreatmentGoalReviewed: Yup.bool().nullable().when([], {
      is: () => !!isFollowUpTreatmentGoalReviewRequired,
      then: (schema) => schema.oneOf([ true ], ' ').required(' '),
    }),
    treatment: Yup.array().of(Yup.object().shape({
      treatmentApproachId: Yup.string().nullable().required(l10n.clinicalNotes.validations.treatmentApproach),
      interventionIds: Yup.array().of(Yup.string()).min(1, l10n.clinicalNotes.validations.treatmentIntervention)
    })),
    treatmentHomework: Yup.string()
      .when('treatmentHomeworkYesNo', {
        is: YesNo.Yes,
        then: (schema) => schema.required(l10n.clinicalNotes.validations.homework)
      }),
    recommendationConfirmation: Yup.bool()
      .when('template', {
        is: 'followUp',
        then: (schema) => schema.when(
          '$requireConfirmation', // access context
          (([ requireConfirmation ], schema: Yup.BooleanSchema<boolean, Yup.AnyObject, boolean>) => {
            // requireConfirmation will be 'y' or 'n'
            if (requireConfirmation === 'y') {
              return schema.oneOf([ true ], 'required');
            } else return schema;
          }))
      }),
  }).concat(dynamicYesNoSchema);
};
export type ClinicalNotesErrorsObjectType = {
  formikErrors: FormikErrors<ClinicalNotesFormType>;
  formikTouched: FormikTouched<ClinicalNotesFormType>;
  hasErrors: boolean;
};

type FormFieldErrorsReducerInitialValueType = {
  errors: FormikErrors<ClinicalNotesFormType[ClinicalNotesDynamicFormKeyType]>;
  touched: FormikTouched<ClinicalNotesFormType[ClinicalNotesDynamicFormKeyType]>;
};

type FormErrorsReducerInitialValueType = Omit<ClinicalNotesErrorsObjectType, 'hasErrors'>;

export const getClinicalNotesDynamicSectionsErrorsObject = (sessionResource: SessionResource): ClinicalNotesErrorsObjectType => {
  const { mseErrors, riskAssessmentErrors, initialProgressErrors, followUpProgressErrors, presentingProblemsErrors } = sessionResource;

  const errorObject: Record<ClinicalNotesDynamicFormKeyType, SessionFormFieldError[]> = {
    mentalStatusExam: mseErrors,
    riskAssessment: riskAssessmentErrors,
    initialProgress: initialProgressErrors,
    followUpProgress: followUpProgressErrors,
    presentingProblems: presentingProblemsErrors,
  };

  let formHasErrors = false;
  // loops through all form field groups and creates error&touched obj structure for formik
  const { formikErrors, formikTouched } = Object.keys(errorObject).reduce((r, errorObjectKey: ClinicalNotesDynamicFormKeyType) => {
    const formFieldErrors = errorObject[errorObjectKey];
    const formFieldGroupHasErrors = !!formFieldErrors?.length;

    // sets parent variable formHasErrors if any field groups contain errors
    if (!formHasErrors && formFieldGroupHasErrors) {
      formHasErrors = formFieldGroupHasErrors;
    }
    // skips if form field group does not have errors
    if (!formFieldGroupHasErrors) {
      return r;
    }

    // NESTED REDUCER
    // gets errors and touched per form field group
    const { errors, touched } = (formFieldErrors ?? []).reduce((r, fieldError) => {
      return {
        errors: { ...r.errors, [fieldError.id]: fieldError.message },
        touched: { ...r.touched, [fieldError.id]: true }
      };
    }, { errors: {}, touched: {} } as FormFieldErrorsReducerInitialValueType);
      // END NESTED REDUCER

    return {
      formikErrors: { ...r.formikErrors, [errorObjectKey]: errors },
      formikTouched: { ...r.formikTouched, [errorObjectKey]: touched }
    };
  }, { formikErrors: {}, formikTouched: {} } as FormErrorsReducerInitialValueType);

  // including touched if product ever decides that errors should show on render
  return { formikErrors, hasErrors: formHasErrors, formikTouched };
};

// this basically takes the formikErrors (created above) and checks each field for changes (changes = no more error)
export const validateClinicalNotesDynamicSections = (
  errorObj: ClinicalNotesErrorsObjectType['formikErrors'],
  values: ClinicalNotesFormType,
  initialValues: ClinicalNotesFormType,
  fieldHash: ClinicalNotesDynamicFormFieldHash,
) => {
  const template = values.template;

  const { dynamicForms, dynamicFormsKeys } = getDynamicFormsObjectFromFormObject(values);

  return dynamicFormsKeys.reduce((previousFormikErrors, dynamicFormKey) => {
    const dynamicForm = dynamicForms[dynamicFormKey];

    const formDisabledByYesNoToggle = checkIfFormDisabledByYesNoToggle(dynamicFormKey, values);
    const formIsNotOnThisTemplate = !checkIfFormIsOnTemplate(dynamicFormKey, template);
    const disableErrorsForThisForm = formDisabledByYesNoToggle || formIsNotOnThisTemplate;

    if (disableErrorsForThisForm) {
      return previousFormikErrors;
    }

    const newObj = Object.keys(dynamicForm).reduce((previousFormErrors, fieldKey) => {
      const formField = fieldHash[dynamicFormKey][fieldKey];

      const value = dynamicForm[fieldKey];
      const initialValue = initialValues[dynamicFormKey][fieldKey];

      let fieldError: string | null = null;

      if (formField.toggleVisibility) {
        const fieldHelpers = getFieldTypeHelpers(formField);
        const yesNoKey = getYesNoFieldKeyFromFieldKey(fieldKey, dynamicFormKey);
        const shouldThrow = values[yesNoKey] === getYesNoFromBooleanValue(formField.toggleVisibility.visibleOnValue) && fieldHelpers.checkIfValueIsEmpty(value);

        if (shouldThrow) {
          fieldError = formField.emptyFieldErrorMessage;
        }
      } else if (errorObj?.[dynamicFormKey]?.[fieldKey]) {
        const shouldThrow = isEqual(value, initialValue);

        if (shouldThrow) {
          fieldError = errorObj[dynamicFormKey][fieldKey];
        }
      }

      return fieldError ? { ...previousFormErrors, [fieldKey]: fieldError } : previousFormErrors;
    }, {} as FormikErrors<ClinicalNotesFormType[ClinicalNotesDynamicFormKeyType]>);

    if (!Object.keys(newObj).length) return previousFormikErrors;
    return { ...previousFormikErrors, [dynamicFormKey]: newObj };
  }, {} as FormikErrors<ClinicalNotesFormType>);
};
